import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AdminPagination } from '@/components/AdminPagination';
import { AdminPill, type AdminPillStatus } from '@/components/AdminPill';
import { AdminTableSkeletonRows } from '@/components/AdminTableSkeletonRows';
import { Skeleton } from '@/components/Skeleton';
import { AdminTableToolbar } from '@/components/AdminTableToolbar';
import { useAdminTablePagination } from '@/hooks/useAdminTablePagination';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { parsePageParam, parsePageSizeParam } from '@/lib/pagination';
import {
  acceptSubmission,
  AcceptSubmissionFailure,
  getAdminSubmissionById,
  nextAllowedSubmissionStatuses,
  updateSubmissionStatus,
  type AcceptSubmissionError,
  type AdminSubmissionDetail,
  type AdminSubmissionStatus,
} from '@/lib/db/admin/submissions';
import { formatGstDate, formatGstDateTimeIso } from '@/lib/format';
import {
  getSellerSubmissionPhotoUrls,
  type SellerPhotoSignedEntry,
} from '@/lib/storage/seller-photos';

import { useAdminSubmissions, type AdminSubmissionsFilter } from './AdminSubmissionsPage.data';

const GalleryLightbox = lazy(() => import('@/components/DealGallery/GalleryLightbox'));

const FILTERS = ['all', 'new', 'in_progress', 'accepted', 'refused'] as const;
type FilterKey = (typeof FILTERS)[number];

const FILTER_I18N_KEY: Record<FilterKey, string> = {
  all: 'admin.submissions.filters.all',
  new: 'admin.submissions.filters.new',
  in_progress: 'admin.submissions.filters.inProgress',
  accepted: 'admin.submissions.filters.accepted',
  refused: 'admin.submissions.filters.refused',
};

const STATUS_PILL_I18N_KEY: Record<AdminSubmissionStatus, string> = {
  new: 'admin.common.newPill',
  in_progress: 'admin.common.inProgressPill',
  accepted: 'admin.common.acceptedPill',
  refused: 'admin.common.refusedPill',
};

const TYPE_I18N_KEY: Record<string, string> = {
  ready: 'admin.submissions.types.ready',
  off_plan: 'admin.submissions.types.off_plan',
};

const REFUSAL_REASON_MIN = 20;

const PRICE_FORMATTER = new Intl.NumberFormat('en-AE', {
  style: 'currency',
  currency: 'AED',
  maximumFractionDigits: 0,
});

function pillVariant(s: AdminSubmissionStatus): AdminPillStatus {
  if (s === 'in_progress') return 'IN_PROGRESS';
  if (s === 'accepted') return 'ACCEPTED';
  if (s === 'refused') return 'REFUSED';
  return 'NEW';
}

function isPostgrestForwardViolation(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { code?: unknown }).code === '23514';
}

function formatPrice(n: number | string | null | undefined): string {
  if (n == null) return '—';
  const value = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(value)) return '—';
  return PRICE_FORMATTER.format(value);
}

export function AdminSubmissionsPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminSubmissionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<Error | null>(null);
  const [photoUrls, setPhotoUrls] = useState<SellerPhotoSignedEntry[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photosError, setPhotosError] = useState<Error | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [refuseModalOpen, setRefuseModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<AdminSubmissionStatus | ''>('');
  const [refuseReason, setRefuseReason] = useState('');
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  // URL is the pagination source of truth (read every render — Back/Forward
  // replays states natively). Writes go through useAdminTablePagination.
  const [searchParams] = useSearchParams();
  const page = parsePageParam(searchParams.get('page'));
  const pageSize = parsePageSizeParam(searchParams.get('pageSize'));
  const debouncedSearch = useDebouncedValue(searchQuery);

  const queryFilter: AdminSubmissionsFilter = useMemo(
    () => ({
      status: filter === 'all' ? 'all' : filter,
      searchQuery: debouncedSearch,
      page,
      pageSize,
    }),
    [filter, debouncedSearch, page, pageSize],
  );

  const { rows, total, newCount, status, error, refetch } = useAdminSubmissions(queryFilter);

  const { goPage, setPageSize } = useAdminTablePagination({
    page,
    pageSize,
    total,
    isSuccess: status === 'success',
    filterKey: `${filter}|${debouncedSearch}`,
  });

  const selectedRow = useMemo(
    () => (openId ? (rows.find((r) => r.id === openId) ?? null) : null),
    [openId, rows],
  );

  // Single teardown path: also resets the accept/refuse/confirm modals,
  // the staged status/reason and the lightbox — those overlays render at
  // the top level (not gated on selectedRow), so releasing only openId
  // would orphan them over an empty page.
  const closeDrawer = useCallback(() => {
    setOpenId(null);
    setAcceptModalOpen(false);
    setRefuseModalOpen(false);
    setConfirmModalOpen(false);
    setPendingStatus('');
    setRefuseReason('');
    setLightboxOpen(false);
  }, []);

  // The open drawer tracks a row of the CURRENT page: when a page / filter
  // change (or a concurrent admin action surfaced by a refetch) drops its
  // row, run the full teardown. Only after a successful load — a transient
  // skeleton state must not slam the drawer shut.
  useEffect(() => {
    if (status !== 'success' || !openId) return;
    if (!rows.some((r) => r.id === openId)) closeDrawer();
  }, [status, rows, openId, closeDrawer]);

  useEffect(() => {
    if (!openId) {
      setDetail(null);
      setPhotoUrls([]);
      setDetailError(null);
      setPhotosError(null);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setPhotosError(null);

    getAdminSubmissionById(openId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        if (d?.photos.length) {
          setPhotosLoading(true);
          getSellerSubmissionPhotoUrls(d.photos)
            .then((urls) => {
              if (!cancelled) setPhotoUrls(urls);
            })
            .catch((err: unknown) => {
              if (!cancelled) {
                setPhotosError(err instanceof Error ? err : new Error(String(err)));
                toast.error('Failed to load submission photos.');
              }
            })
            .finally(() => {
              if (!cancelled) setPhotosLoading(false);
            });
        } else {
          setPhotoUrls([]);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDetailError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [openId]);

  const handleValidateClick = () => {
    if (!pendingStatus) return;
    if (pendingStatus === 'accepted') {
      setAcceptModalOpen(true);
      return;
    }
    if (pendingStatus === 'refused') {
      setRefuseModalOpen(true);
      return;
    }
    setConfirmModalOpen(true);
  };

  const confirmGenericChange = async () => {
    if (!selectedRow || !pendingStatus) return;
    setIsChangingStatus(true);
    try {
      await updateSubmissionStatus({ id: selectedRow.id, toStatus: pendingStatus });
      toast.success(t('admin.submissions.statusUpdatedToast'));
      setConfirmModalOpen(false);
      setPendingStatus('');
      refetch();
    } catch (err) {
      if (isPostgrestForwardViolation(err)) {
        toast.error(t('admin.submissions.errors.forbiddenTransition'));
      } else {
        toast.error(t('admin.submissions.errors.generic'));
      }
    } finally {
      setIsChangingStatus(false);
    }
  };

  const confirmAccept = async () => {
    if (!selectedRow) return;
    setIsChangingStatus(true);
    try {
      const result = await acceptSubmission({ id: selectedRow.id });
      const reference = result.deal_reference_id;
      if (result.photos_copy_status === 'failed') {
        toast.warning(t('admin.submissions.acceptedToastPhotoFailed', { reference }));
      } else {
        toast.success(t('admin.submissions.acceptedToast', { reference }));
      }
      setAcceptModalOpen(false);
      setPendingStatus('');
      refetch();
    } catch (err) {
      if (err instanceof AcceptSubmissionFailure) {
        const code: AcceptSubmissionError = err.errorCode;
        const i18nKey =
          code === 'forbidden_status_transition'
            ? 'admin.submissions.errors.forbiddenTransition'
            : code === 'submission_not_found'
              ? 'admin.submissions.errors.submissionNotFound'
              : code === 'non_admin'
                ? 'admin.submissions.errors.nonAdmin'
                : 'admin.submissions.errors.generic';
        toast.error(t(i18nKey));
      } else {
        toast.error(t('admin.submissions.errors.generic'));
      }
    } finally {
      setIsChangingStatus(false);
    }
  };

  const confirmRefuse = async () => {
    if (!selectedRow) return;
    if (refuseReason.trim().length < REFUSAL_REASON_MIN) return;
    setIsChangingStatus(true);
    try {
      await updateSubmissionStatus({
        id: selectedRow.id,
        toStatus: 'refused',
        refusalReason: refuseReason.trim(),
      });
      toast.success(t('admin.submissions.refusedToast'));
      setRefuseModalOpen(false);
      setRefuseReason('');
      setPendingStatus('');
      refetch();
    } catch (err) {
      if (isPostgrestForwardViolation(err)) {
        toast.error(t('admin.submissions.errors.forbiddenTransition'));
      } else {
        toast.error(t('admin.submissions.errors.generic'));
      }
    } finally {
      setIsChangingStatus(false);
    }
  };

  const lightboxSlides = photoUrls.map((p) => ({
    src: p.signedUrl,
    alt: t('admin.submissions.photos.openLightbox', { position: p.position + 1 }),
  }));

  return (
    <>
      <header className="admin-head">
        <div>
          <h1 className="admin-head__title">{t('admin.submissions.title')}</h1>
          <div className="admin-head__sub">
            {t('admin.submissions.summary', { total, newCount })}
          </div>
        </div>
      </header>

      <AdminTableToolbar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('admin.common.searchPlaceholder')}
      >
        {FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            className={`admin-filter-tab${filter === key ? ' is-active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {t(FILTER_I18N_KEY[key])}
          </button>
        ))}
      </AdminTableToolbar>

      {status === 'loading' && (
        <div className="admin-table-wrap" data-testid="admin-submissions-loading" aria-busy="true">
          <table className="admin-table">
            <tbody>
              <AdminTableSkeletonRows columns={9} />
            </tbody>
          </table>
        </div>
      )}

      {status === 'error' && (
        <div className="admin-text-muted" data-testid="admin-submissions-error" role="alert">
          {t('admin.submissions.errors.list')}
          {error?.message ? ` (${error.message})` : null}
        </div>
      )}

      {status === 'success' && rows.length === 0 && (
        <div className="admin-text-muted" data-testid="admin-submissions-empty">
          {t('admin.submissions.empty.title')}
        </div>
      )}

      {status === 'success' && rows.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table admin-table--sticky-actions">
            <thead>
              <tr>
                <th>{t('admin.submissions.headers.date')}</th>
                <th>{t('admin.submissions.headers.reference')}</th>
                <th>{t('admin.submissions.headers.seller')}</th>
                <th>{t('admin.submissions.headers.email')}</th>
                <th>{t('admin.submissions.headers.type')}</th>
                <th>{t('admin.submissions.headers.zone')}</th>
                <th className="admin-cell--right">{t('admin.submissions.headers.price')}</th>
                <th>{t('admin.submissions.headers.status')}</th>
                <th>{t('admin.submissions.headers.action')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((sub) => (
                <tr key={sub.id}>
                  <td className="admin-cell--mono">{formatGstDate(sub.created_at)}</td>
                  <td className="admin-cell--mono">{sub.reference_id}</td>
                  <td>
                    <span className="admin-cell-ellipsis" title={sub.seller_full_name}>
                      {sub.seller_full_name}
                    </span>
                    {sub.duplicate_suspected ? (
                      <span
                        className="admin-dup-icon"
                        data-testid="admin-submissions-row-dup"
                        aria-label={t('admin.submissions.duplicateSuspected.rowAria')}
                        title={t('admin.submissions.duplicateSuspected.drawerHint')}
                        style={{
                          marginInlineStart: 6,
                          color: 'var(--ddm-warning, #d97706)',
                        }}
                      >
                        ⚠
                      </span>
                    ) : null}
                  </td>
                  <td className="admin-cell--mono">
                    <span className="admin-cell-ellipsis" title={sub.seller_email}>
                      {sub.seller_email}
                    </span>
                  </td>
                  <td>{t(TYPE_I18N_KEY[sub.type] ?? sub.type)}</td>
                  <td>{sub.zone}</td>
                  <td className="admin-cell--mono admin-cell--right">
                    {formatPrice(sub.desired_asking_price)}
                  </td>
                  <td>
                    <AdminPill
                      status={pillVariant(sub.status)}
                      label={t(STATUS_PILL_I18N_KEY[sub.status])}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="admin-cell-link"
                      onClick={() => setOpenId(sub.id)}
                      aria-label={t('admin.submissions.openDrawer')}
                    >
                      {t('admin.submissions.open')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {status !== 'error' && (
        <AdminPagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={goPage}
          onPageSizeChange={setPageSize}
          idPrefix="admin-submissions"
        />
      )}

      {selectedRow ? (
        <div className="admin-detail-modal" role="presentation" onClick={closeDrawer}>
          <div
            className="admin-detail-modal__inner"
            role="dialog"
            aria-modal="true"
            aria-label={t('admin.submissions.drawer.aria')}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-drawer__head">
              <span className="admin-drawer__id">{selectedRow.reference_id}</span>
              <button
                type="button"
                className="admin-drawer__close"
                onClick={closeDrawer}
                aria-label={t('admin.submissions.drawer.close')}
              >
                ×
              </button>
            </div>

            <div className="admin-drawer__title">
              {selectedRow.seller_full_name} · {selectedRow.zone}
            </div>

            {detailLoading ? <Skeleton width="50%" height={14} /> : null}

            {detailError ? (
              <div className="admin-text-muted" role="alert">
                {t('admin.submissions.errors.generic')}
              </div>
            ) : null}

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.submissions.drawer.property')}
              </div>
              <div className="admin-drawer__row-value">
                <div>
                  <strong>{t('admin.submissions.drawer.community')}:</strong>{' '}
                  {selectedRow.community}
                </div>
                {selectedRow.building ? (
                  <div>
                    <strong>{t('admin.submissions.drawer.building')}:</strong>{' '}
                    {selectedRow.building}
                  </div>
                ) : null}
                <div>
                  <strong>{t('admin.submissions.drawer.bedrooms')}:</strong> {selectedRow.bedrooms}
                </div>
                {selectedRow.area_sqft != null ? (
                  <div>
                    <strong>{t('admin.submissions.drawer.area')}:</strong>{' '}
                    {selectedRow.area_sqft.toLocaleString('en-AE')}
                  </div>
                ) : null}
                <div>
                  <strong>{t('admin.submissions.drawer.askingPrice')}:</strong>{' '}
                  {formatPrice(selectedRow.desired_asking_price)}
                </div>
                {selectedRow.type === 'off_plan' ? (
                  <>
                    {selectedRow.original_price != null ? (
                      <div>
                        <strong>{t('admin.submissions.drawer.originalPrice')}:</strong>{' '}
                        {formatPrice(selectedRow.original_price)}
                      </div>
                    ) : null}
                    {selectedRow.amount_paid != null ? (
                      <div>
                        <strong>{t('admin.submissions.drawer.amountPaid')}:</strong>{' '}
                        {formatPrice(selectedRow.amount_paid)}
                      </div>
                    ) : null}
                  </>
                ) : null}
                {selectedRow.duplicate_suspected ? (
                  <div
                    role="status"
                    data-testid="admin-submissions-dup-badge"
                    style={{
                      marginTop: 8,
                      padding: '6px 10px',
                      background: 'var(--ddm-warning-bg, #fef3c7)',
                      color: 'var(--ddm-warning, #92400e)',
                      borderRadius: 4,
                    }}
                  >
                    <strong>⚠ {t('admin.submissions.duplicateSuspected.badge')}</strong>
                    <div style={{ fontSize: 12, marginTop: 2 }}>
                      {t('admin.submissions.duplicateSuspected.drawerHint')}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">{t('admin.submissions.drawer.contact')}</div>
              <div className="admin-drawer__row-value">
                <div>{selectedRow.seller_email}</div>
                <div className="admin-text-muted">{selectedRow.seller_phone}</div>
              </div>
            </div>

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.submissions.drawer.submittedAt')}
              </div>
              <div className="admin-drawer__row-value">
                {formatGstDateTimeIso(selectedRow.created_at)}
              </div>
            </div>

            {selectedRow.reason_for_selling ? (
              <div className="admin-drawer__row">
                <div className="admin-drawer__row-label">
                  {t('admin.submissions.drawer.reasonForSelling')}
                </div>
                <div className="admin-drawer__row-value">{selectedRow.reason_for_selling}</div>
              </div>
            ) : null}

            {selectedRow.description ? (
              <div className="admin-drawer__row">
                <div className="admin-drawer__row-label">
                  {t('admin.submissions.drawer.description')}
                </div>
                <div className="admin-drawer__row-value" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedRow.description}
                </div>
              </div>
            ) : null}

            {detail?.treated_by_user ? (
              <div className="admin-drawer__row">
                <div className="admin-drawer__row-label">
                  {t('admin.submissions.drawer.treatedBy')}
                </div>
                <div className="admin-drawer__row-value">
                  {[detail.treated_by_user.first_name, detail.treated_by_user.last_name]
                    .filter(Boolean)
                    .join(' ') || detail.treated_by_user.email}
                  {selectedRow.treated_at ? (
                    <span className="admin-text-muted" style={{ marginInlineStart: 8 }}>
                      · {formatGstDateTimeIso(selectedRow.treated_at)}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}

            {detail?.converted_deal ? (
              <div className="admin-drawer__row">
                <div className="admin-drawer__row-label">
                  {t('admin.submissions.drawer.convertedDeal')}
                </div>
                <div className="admin-drawer__row-value">
                  <Link to={`/admin/deals/${detail.converted_deal.id}/edit`}>
                    {detail.converted_deal.reference_id}
                  </Link>
                </div>
              </div>
            ) : null}

            {selectedRow.status === 'refused' && selectedRow.refusal_reason ? (
              <div className="admin-drawer__row">
                <div className="admin-drawer__row-label">
                  {t('admin.submissions.refuseModal.label')}
                </div>
                <div className="admin-drawer__row-value" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedRow.refusal_reason}
                </div>
              </div>
            ) : null}

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.submissions.photos.title', {
                  count: detail?.photos.length ?? selectedRow.photos_count ?? 0,
                })}
              </div>
              <div className="admin-drawer__row-value">
                {photosLoading ? (
                  <Skeleton width="50%" height={14} />
                ) : photosError ? (
                  <div className="admin-text-muted" role="alert">
                    {t('admin.submissions.photos.error')}
                  </div>
                ) : photoUrls.length === 0 ? (
                  <div className="admin-text-muted">{t('admin.submissions.photos.empty')}</div>
                ) : (
                  <div
                    data-testid="admin-submissions-photos-grid"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                      gap: 6,
                    }}
                  >
                    {photoUrls.map((p, idx) => (
                      <button
                        key={p.id}
                        type="button"
                        className="admin-photo-thumb"
                        onClick={() => {
                          setLightboxIndex(idx);
                          setLightboxOpen(true);
                        }}
                        aria-label={t('admin.submissions.photos.openLightbox', {
                          position: p.position + 1,
                        })}
                        style={{
                          padding: 0,
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <img
                          src={p.signedUrl}
                          alt=""
                          loading="lazy"
                          style={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="admin-drawer__row">
              <div className="admin-drawer__row-label">
                {t('admin.submissions.changeStatus.label')}
              </div>
              <div className="admin-drawer__row-value">
                {(() => {
                  const allowed = nextAllowedSubmissionStatuses(selectedRow.status);
                  if (allowed.length === 0) {
                    return (
                      <span className="admin-text-muted">
                        {t('admin.submissions.changeStatus.terminal')}
                      </span>
                    );
                  }
                  return (
                    <select
                      className="admin-select"
                      value={pendingStatus}
                      onChange={(e) =>
                        setPendingStatus(e.target.value as AdminSubmissionStatus | '')
                      }
                      disabled={isChangingStatus}
                      aria-label={t('admin.submissions.changeStatus.label')}
                    >
                      <option value="" disabled>
                        {t('admin.submissions.changeStatus.placeholder')}
                      </option>
                      {allowed.map((s) => (
                        <option key={s} value={s}>
                          {t(STATUS_PILL_I18N_KEY[s])}
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>
            </div>

            {nextAllowedSubmissionStatuses(selectedRow.status).length > 0 ? (
              <div
                className="admin-drawer__row"
                style={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                <button
                  type="button"
                  className="admin-btn"
                  onClick={handleValidateClick}
                  disabled={!pendingStatus || isChangingStatus}
                  data-testid="admin-submission-validate"
                >
                  {t('admin.submissions.changeStatus.validateButton')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <Suspense fallback={null}>
        <GalleryLightbox
          open={lightboxOpen}
          index={lightboxIndex}
          slides={lightboxSlides}
          close={() => setLightboxOpen(false)}
        />
      </Suspense>

      {acceptModalOpen ? (
        <div
          className="admin-refuse-modal"
          role="presentation"
          onClick={() => setAcceptModalOpen(false)}
        >
          <div
            className="admin-refuse-modal__inner"
            role="dialog"
            aria-modal="true"
            aria-label={t('admin.submissions.acceptModal.title')}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="admin-refuse-modal__title">
              {t('admin.submissions.acceptModal.title')}
            </h3>
            <p className="admin-text-muted" style={{ marginBottom: 16 }}>
              {t('admin.submissions.acceptModal.body')}
            </p>
            <div className="admin-refuse-modal__actions">
              <button
                type="button"
                className="admin-btn admin-btn--outline"
                onClick={() => setAcceptModalOpen(false)}
                disabled={isChangingStatus}
              >
                {t('admin.submissions.acceptModal.cancel')}
              </button>
              <button
                type="button"
                className="admin-btn"
                onClick={() => void confirmAccept()}
                disabled={isChangingStatus}
              >
                {t('admin.submissions.acceptModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {refuseModalOpen ? (
        <div
          className="admin-refuse-modal"
          role="presentation"
          onClick={() => {
            setRefuseModalOpen(false);
            setRefuseReason('');
          }}
        >
          <div
            className="admin-refuse-modal__inner"
            role="dialog"
            aria-modal="true"
            aria-label={t('admin.submissions.refuseModal.title')}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="admin-refuse-modal__title">
              {t('admin.submissions.refuseModal.title')}
            </h3>
            <div className="admin-fieldset-label" style={{ marginBottom: 8 }}>
              {t('admin.submissions.refuseModal.label')} *
            </div>
            <textarea
              className="admin-refuse-modal__textarea"
              rows={4}
              value={refuseReason}
              onChange={(event) => setRefuseReason(event.target.value)}
              placeholder={t('admin.submissions.refuseModal.placeholder')}
              aria-label={t('admin.submissions.refuseModal.label')}
            />
            <div className="admin-refuse-modal__hint">
              {t('admin.submissions.refuseModal.hint', {
                count: refuseReason.trim().length,
              })}
            </div>
            <div className="admin-refuse-modal__actions">
              <button
                type="button"
                className="admin-btn admin-btn--outline"
                onClick={() => {
                  setRefuseModalOpen(false);
                  setRefuseReason('');
                }}
                disabled={isChangingStatus}
              >
                {t('admin.submissions.refuseModal.cancel')}
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--danger"
                disabled={refuseReason.trim().length < REFUSAL_REASON_MIN || isChangingStatus}
                onClick={() => void confirmRefuse()}
              >
                {t('admin.submissions.refuseModal.send')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmModalOpen && pendingStatus ? (
        <div
          className="admin-refuse-modal"
          role="presentation"
          onClick={() => setConfirmModalOpen(false)}
        >
          <div
            className="admin-refuse-modal__inner"
            role="dialog"
            aria-modal="true"
            aria-label={t('admin.submissions.confirmModal.title')}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="admin-refuse-modal__title">
              {t('admin.submissions.confirmModal.title')}
            </h3>
            <p className="admin-text-muted" style={{ marginBottom: 16 }}>
              {t('admin.submissions.confirmModal.body', {
                status: t(STATUS_PILL_I18N_KEY[pendingStatus]),
              })}
            </p>
            <div className="admin-refuse-modal__actions">
              <button
                type="button"
                className="admin-btn admin-btn--outline"
                onClick={() => setConfirmModalOpen(false)}
                disabled={isChangingStatus}
              >
                {t('admin.submissions.confirmModal.cancel')}
              </button>
              <button
                type="button"
                className="admin-btn"
                onClick={() => void confirmGenericChange()}
                disabled={isChangingStatus}
              >
                {t('admin.submissions.confirmModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
